import { useTranslation } from 'next-i18next';
import { FilterType, freeFormHubFilterKeys } from '../../types';
import { FilterWrapper } from '../filterWrapper/filterWrapper';
import { useFreeFormHubFilterState } from '../../hooks/useUrlFilterState';
import { useFreeFormHubInputValidator } from '../../hooks/inputValidator';
import { useMemo, useState, useEffect } from 'react';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { ffhEventCategory, TrackedFfhEventActions } from '@hub/free-form-hub/tracking.config';
import type { Procedure } from '@igus/iglidur-free-form-hub-interfaces';

type MethodSectionProps = {
  procedure: Procedure[];
};

type FilterOption = {
  label: Procedure;
  value: Procedure;
};

export const MethodSection = ({ procedure }: MethodSectionProps) => {
  const { t } = useTranslation(['category/productFilter', 'common']);
  const { CustomEvent } = usePiwikPro();
  const [options, setOptions] = useState<FilterOption[]>([]);
  const multiselectValidator = useFreeFormHubInputValidator(FilterType.AUTOCOMPLETE);
  const [values, setValue] = useFreeFormHubFilterState<FilterType.AUTOCOMPLETE>(
    freeFormHubFilterKeys.PRODUCTION_METHOD,
    multiselectValidator
  );

  const getFilterOptions = (procedureList: Procedure[]): FilterOption[] =>
    procedureList.map(item => ({
      label: t(`PRODUCTION_METHODS.${item.replace(/-/g, '_')}`, { ns: 'free-form-hub/freeFormHub' }) as Procedure,
      value: item,
    }));

  const selectValue = useMemo(
    () => values?.map(val => options.find(opt => opt.value === val)).filter(opt => opt !== undefined) ?? [],
    [values, options]
  );

  const SectionConfig = [
    {
      type: FilterType.AUTOCOMPLETE,
      props: {
        key: freeFormHubFilterKeys.PRODUCTION_METHOD,
        id: freeFormHubFilterKeys.PRODUCTION_METHOD,
        placeholder: t(`FILTERS.PLACEHOLDER.PRODUCTION_METHOD`, { ns: 'free-form-hub/freeFormHub' }),
        options,
        value: selectValue,
        isMulti: true,
        onChange: (changeValue: string[]) => {
          if (changeValue.length) {
            CustomEvent.trackEvent(
              ffhEventCategory,
              TrackedFfhEventActions.filterManufacturingMethodSelect,
              JSON.stringify(changeValue)
            );
          }
          if (!changeValue?.length) {
            setValue(undefined);
            return;
          }

          if (!Array.isArray(changeValue)) {
            setValue([changeValue]);
            return;
          }

          setValue(changeValue ?? undefined);
        },
      },
    },
  ];

  useEffect(() => {
    setOptions(getFilterOptions(procedure));
  }, [procedure]);

  return (
    <>
      {SectionConfig.map(filter => (
        <div className="mt-8" key={filter.props.key}>
          <FilterWrapper type={filter.type} props={filter.props} />
        </div>
      ))}
    </>
  );
};
